import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useData } from "@/context/DataContext";
import api from "@/lib/api";

function useCategories() {
  const { categories } = useData();
  return { categories };
}

export default function Blog() {
  const { t, pick } = useLang();
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  useEffect(() => {
    api.get("/articles", { params: { limit: 100 } }).then(({ data }) => setPosts(data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const { categories } = useCategories();

  const list = useMemo(() => {
    return posts.filter((p) => {
      const okCat = !cat || p.category === cat;
      const needle = q.trim().toLowerCase();
      const okQ = !needle || p.title.toLowerCase().includes(needle) || (p.excerpt || "").toLowerCase().includes(needle);
      return okCat && okQ;
    });
  }, [posts, q, cat]);

  const catName = (id) => pick((categories.find((c) => c.id === id) || {}).name_ar, (categories.find((c) => c.id === id) || {}).name_en) || id;

  return (
    <main>
      <SEO title="مدونة ميرو للسيارات والإطارات | مقالات عربية عن الكفرات والبنشر المتنقل وTPMS" description="مدونة ميرو للسيارات والإطارات: مقالات عربية أصلية عن الكفرات، البنشر المتنقل، حساسات TPMS، مقاسات الإطارات، البطاريات، والسلامة على الطريق." path="/blog" />
      <PageHero eyebrow="MERO BLOG" title={t("blog.title")} sub={t("blog.sub")} />

      <section className="pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="relative mb-8 max-w-xl">
              <Search size={18} className="absolute top-1/2 -translate-y-1/2 text-dim" style={{ insetInlineStart: "1rem" }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("blog.search")} className="field !ps-11" data-testid="blog-search-input" />
            </div>
            <div className="mb-10 flex flex-wrap gap-2.5" data-testid="blog-category-filters">
              <button onClick={() => setCat("")} className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${!cat ? "bg-gold text-ink" : "border border-white/12 bg-coal/60 text-zinc-300 hover:border-gold/50"}`} data-testid="blog-cat-all">{t("blog.all")}</button>
              {categories.map((c) => (
                <button key={c.id} onClick={() => setCat(c.id)} className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${cat === c.id ? "bg-gold text-ink" : "border border-white/12 bg-coal/60 text-zinc-300 hover:border-gold/50"}`} data-testid={`blog-cat-${c.id}`}>
                  {pick(c.name_ar, c.name_en)}
                </button>
              ))}
            </div>
          </Reveal>

          {list.length === 0 && <p className="rounded-3xl border border-white/10 bg-coal/60 p-10 text-center text-mist">{t("blog.noResults")}</p>}

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
            {list.map((p, i) => (
              <Reveal key={p.slug} delay={i % 3}>
                <Link to={`/blog/${p.slug}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-coal/60 transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/40" data-testid={`blog-card-${p.slug}`}>
                  <div className="h-48 overflow-hidden">
                    <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="mb-3 flex items-center gap-3 text-xs">
                      <span className="rounded-full bg-gold/10 px-2.5 py-1 font-bold text-gold">{catName(p.category)}</span>
                      <span className="flex items-center gap-1 text-dim"><Clock size={11} /> {p.read_time} {t("common.mins")}</span>
                    </div>
                    <h2 className="flex-1 text-base font-bold leading-8 text-white group-hover:text-gold">{p.title}</h2>
                    <p className="mt-3 line-clamp-2 text-sm leading-7 text-mist">{p.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

